import React, { useState } from 'react';
import { Calendar, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import WheelRadarChart from './WheelRadarChart';

function WheelHistory({ assessments, currentAssessment, onSelectAssessment, onCompare }) {
  const [selectedForComparison, setSelectedForComparison] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAverageScore = (scores) => {
    if (!scores || scores.length === 0) return 0;
    return (scores.reduce((sum, s) => sum + s.score, 0) / scores.length).toFixed(1);
  };

  const getScoreChange = (current, previous) => {
    if (!current || !previous) return null;
    const currentAvg = parseFloat(getAverageScore(current.scores));
    const prevAvg = parseFloat(getAverageScore(previous.scores));
    const diff = currentAvg - prevAvg;
    return diff.toFixed(1);
  };

  const handleCompareToggle = (assessment) => {
    if (selectedForComparison?.id === assessment.id) {
      setSelectedForComparison(null);
      if (onCompare) onCompare(null);
    } else {
      setSelectedForComparison(assessment);
      if (onCompare) onCompare(assessment);
    }
  };

  return (
    <div className="space-y-4">
      {/* Timeline view */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Assessment History
        </h3>

        {assessments.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-500" />
            <p className="text-gray-400">No assessments yet</p>
            <p className="text-sm text-gray-500 mt-2">Create your first RMMBR Circle assessment to track your progress</p>
          </div>
        ) : (
          <div className="space-y-3">
            {assessments.map((assessment, index) => {
              const isSelected = currentAssessment?.id === assessment.id;
              const isComparing = selectedForComparison?.id === assessment.id;
              const avgScore = getAverageScore(assessment.scores);
              const prevAssessment = assessments[index + 1];
              const scoreChange = prevAssessment ? getScoreChange(assessment, prevAssessment) : null;

              return (
                <div
                  key={assessment.id}
                  className={`
                    bg-white/5 rounded-xl p-4 border transition-all cursor-pointer
                    ${isSelected ? 'border-[var(--accent)] bg-[var(--accent)]/10' : 'border-white/10 hover:bg-white/10'}
                    ${isComparing ? 'ring-2 ring-[#9b7c54]' : ''}
                  `}
                  onClick={() => onSelectAssessment(assessment)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-medium">{assessment.title}</h4>
                        {index === 0 && (
                          <span className="text-xs bg-[var(--accent)]/20 text-[var(--accent)] px-2 py-0.5 rounded">
                            Latest
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{formatDate(assessment.assessment_date)}</p>
                      {assessment.notes && (
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{assessment.notes}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Average score */}
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[var(--accent)]">{avgScore}</p>
                        <p className="text-xs text-gray-500">avg</p>
                        {scoreChange && (
                          <div className="flex items-center justify-end gap-1 mt-1">
                            {parseFloat(scoreChange) > 0 ? (
                              <>
                                <TrendingUp className="w-3 h-3 text-green-400" />
                                <span className="text-xs text-green-400">+{scoreChange}</span>
                              </>
                            ) : parseFloat(scoreChange) < 0 ? (
                              <>
                                <TrendingDown className="w-3 h-3 text-red-400" />
                                <span className="text-xs text-red-400">{scoreChange}</span>
                              </>
                            ) : (
                              <>
                                <Minus className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-400">0</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Compare button */}
                      {!isSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompareToggle(assessment);
                          }}
                          className={`
                            px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                            ${isComparing
                              ? 'bg-[#9b7c54] text-white'
                              : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }
                          `}
                        >
                          {isComparing ? 'Comparing' : 'Compare'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Comparison view */}
      {selectedForComparison && currentAssessment && (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white">Comparison View</h3>
            <button
              onClick={() => {
                setSelectedForComparison(null);
                if (onCompare) onCompare(null);
              }}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
          <div className="text-sm text-gray-400 mb-4">
            Comparing <span className="text-[var(--accent)] font-medium">{formatDate(currentAssessment.assessment_date)}</span> with{' '}
            <span className="text-[#9b7c54] font-medium">{formatDate(selectedForComparison.assessment_date)}</span>
          </div>
          <WheelRadarChart
            scores={currentAssessment.scores}
            comparisonScores={selectedForComparison.scores}
            showComparison={true}
          />
        </div>
      )}
    </div>
  );
}

export default WheelHistory;
